/**
 * The hard-coded shell rail groups (leaf-route form). Order is significant.
 * These back the multi-page groups; Appearance keeps its in-page anchors and is
 * defined separately in {@link APPEARANCE_NAV_GROUP}.
 */
export const SHELL_SETTINGS_GROUPS = [
    {
        label: "User",
        pages: [
            { href: "/settings/user/profile", label: "Profile" },
            { href: "/settings/user/preferences", label: "Preferences" },
            { href: "/settings/user/notifications", label: "Notifications" },
            {
                href: "/settings/user/keyboard-shortcuts",
                label: "Keyboard shortcuts",
            },
        ],
    },
    {
        label: "Workspace",
        pages: [
            { href: "/settings/workspace", label: "General", activeHref: "/settings/workspace" },
            { href: "/settings/workspace/sections", label: "Sections" },
            { href: "/settings/workspace/data", label: "Data" },
            { href: "/settings/workspace/security", label: "Security" },
            // Lane APPROVESURFACE — where an owner approves a headless machine.
            { href: "/settings/workspace/devices", label: "Device approvals" },
            { href: "/settings/workspace/archive", label: "Archive" },
            // Data Design §6.4: custom domains — compose apps as data (kinds + views +
            // a home under a mode). Enabled domains appear in the mode sub-nav.
            { href: "/settings/workspace/domains", label: "Domains" },
            { href: "/settings/workspace/directory", label: "Directory" },
            // Company identity (domain capture + enrichment) and the Pulse/Health-Wall
            // target calibration + the Use-case advisor — real workspace-config pages
            // that keep their own top-level routes (heavily inbound-linked), listed
            // here as Workspace leaves.
            { href: "/settings/company-profile", label: "Company profile" },
            { href: "/settings/advisor", label: "Use-case advisor" },
            { href: "/settings/pulse", label: "Pulse targets" },
        ],
    },
];
export const INTEGRATIONS_SETTINGS_GROUP = {
    label: "Integrations",
    // One status-first home (catalog + connected accounts) instead of a generic
    // Overview dashboard + a separate Catalog + a Connections list. The folded
    // routes redirect into the home; per-connection detail lives under it. See
    // docs/design/INTEGRATIONS-FLOW-REDESIGN.md. The Sync conflicts queue and the
    // "Connect a database" wizard are real pages that were previously reachable
    // only by deep link — surfaced here so they're navigable.
    pages: [
        {
            href: "/settings/integrations",
            label: "Integrations",
            activeHref: "/settings/integrations",
        },
        { href: "/settings/integrations/activity", label: "Sync activity" },
        { href: "/settings/integrations/conflicts", label: "Sync conflicts" },
        { href: "/settings/integrations/sources/new", label: "Add custom source" },
        { href: "/settings/integrations/database", label: "Connect a database" },
        { href: "/settings/integrations/settings", label: "Settings" },
    ],
};
export const AGENTS_SETTINGS_GROUP = {
    label: "Agents",
    pages: [
        { href: "/settings/agents/clis", label: "Coding Agents" },
        { href: "/settings/agents/models", label: "Models" },
        // The live residency disclosure — a real page that was orphaned from the
        // rail (reachable only by direct URL). Surfaced beside Models.
        {
            href: "/settings/agents/models/residency",
            label: "Model residency",
            activeHref: "/settings/agents/models/residency",
        },
        { href: "/settings/agents/context", label: "Context" },
        { href: "/settings/agents/autopilot", label: "Autopilot" },
        { href: "/settings/agents/auto-verify", label: "Auto-verify" },
        { href: "/settings/agents/optimization", label: "Optimization" },
        { href: "/settings/agents/verdicts", label: "Review routing" },
        { href: "/settings/agents/fleet", label: "Fleet" },
        { href: "/settings/agents/mirror", label: "Repo mirror" },
        { href: "/settings/agents/repos", label: "Repositories" },
        { href: "/settings/agents/notify", label: "Notify" },
        { href: "/settings/agents/inbound", label: "Inbound" },
        { href: "/settings/agents/learning", label: "Learning loop" },
        { href: "/settings/agents/credentials", label: "Credentials & billing" },
        { href: "/settings/agents/copilot", label: "Copilot" },
        { href: "/settings/agents/database", label: "Database assistant" },
        { href: "/settings/agents/monitor", label: "Distribution monitor" },
        { href: "/settings/agents/integrate", label: "Self-integration" },
        { href: "/settings/agents/consent", label: "Browser actions" },
        { href: "/settings/agents/telemetry", label: "Usage analytics" },
    ],
};
export const PERSONAL_SETTINGS_GROUP = {
    label: "Personal",
    pages: [{ href: "/settings/personal", label: "Knowledge corpus" }],
};
// Apps — the local-project launcher/runner config (docs/apps/PRD.md §7). One
// page: the runner kill switch + the localhost intercept toggle.
export const APPS_SETTINGS_GROUP = {
    label: "Apps",
    pages: [{ href: "/settings/apps", label: "Apps" }],
};
export const ADVANCED_SETTINGS_GROUP = {
    label: "Advanced",
    pages: [
        { href: "/settings/advanced/mcp", label: "MCP servers" },
        { href: "/settings/advanced/data", label: "Data & residency" },
        { href: "/settings/advanced/performance", label: "Performance" },
        { href: "/settings/advanced/danger", label: "Danger zone" },
    ],
};
// Multiplayer (members, devices, primary/sync). Overview is the on-ramp in
// single-player and the fleet dashboard once a primary is connected. The route
// stays /settings/workspace/team — this is a display-only rename.
export const MULTIPLAYER_SETTINGS_GROUP = {
    label: "Multiplayer",
    pages: [
        {
            href: "/settings/workspace/team",
            label: "Overview",
            activeHref: "/settings/workspace/team",
        },
        { href: "/settings/workspace/team/members", label: "Members" },
        { href: "/settings/workspace/team/devices", label: "Devices" },
        { href: "/settings/workspace/team/primary", label: "Primary & sync" },
        { href: "/settings/workspace/team/roles", label: "Roles & permissions" },
    ],
};
// Customers — the CRM back-office config, split from one 12-section scroll into
// five navigable sub-pages (docs/settings/PRD.md). Each editor is
// location-agnostic, so the split is content-preserving.
export const CUSTOMERS_SETTINGS_GROUP = {
    label: "Customers",
    pages: [
        { href: "/settings/customers/pipelines", label: "Pipelines & targets" },
        { href: "/settings/customers/leads", label: "Lead taxonomy" },
        { href: "/settings/customers/messaging", label: "Messaging" },
        { href: "/settings/customers/sending", label: "Sending" },
        { href: "/settings/customers/data", label: "Data" },
    ],
};
/**
 * User — a multi-page group. Profile, Preferences, Notifications, and Keyboard
 * shortcuts each get their own route (the leaf routes existed as redirects into
 * a single scroll page; v4 makes them real, navigable pages). `/settings/user`
 * redirects to Profile.
 */
const USER_NAV_GROUP = {
    key: "user",
    label: "User",
    iconKey: "User",
    href: "/settings/user/profile",
    consolidated: false,
    items: [
        { href: "/settings/user/profile", label: "Profile" },
        { href: "/settings/user/preferences", label: "Preferences" },
        { href: "/settings/user/notifications", label: "Notifications" },
        {
            href: "/settings/user/keyboard-shortcuts",
            label: "Keyboard shortcuts",
        },
    ],
};
/**
 * Workspace — a multi-page group (the #4 fix). Every former anchored section
 * (General/name+vocabulary, Sections, Data, Security, Archive) is now its own
 * route, alongside the heavier workspace-config pages (Domains, Directory,
 * Company profile, Use-case advisor, Pulse targets) that always were routes.
 * No more mid-scroll off-page jumps.
 */
const WORKSPACE_NAV_GROUP = {
    key: "workspace",
    label: "Workspace",
    iconKey: "Building2",
    href: "/settings/workspace",
    consolidated: false,
    items: [
        { href: "/settings/workspace", label: "General", activeHref: "/settings/workspace" },
        { href: "/settings/workspace/sections", label: "Sections" },
        { href: "/settings/workspace/data", label: "Data" },
        { href: "/settings/workspace/security", label: "Security" },
        { href: "/settings/workspace/devices", label: "Device approvals" },
        { href: "/settings/workspace/archive", label: "Archive" },
        { href: "/settings/workspace/domains", label: "Domains" },
        { href: "/settings/workspace/directory", label: "Directory" },
        {
            href: "/settings/company-profile",
            label: "Company profile",
            activeHref: "/settings/company-profile",
        },
        {
            href: "/settings/advisor",
            label: "Use-case advisor",
            activeHref: "/settings/advisor",
        },
        { href: "/settings/pulse", label: "Pulse targets", activeHref: "/settings/pulse" },
    ],
};
/**
 * Appearance — the ONE consolidated single-page group. Theme/Density/Accent are
 * in-page anchors on one small page that never navigates off itself, so
 * scroll-spy is honest here (not the #4 bug).
 */
const APPEARANCE_NAV_GROUP = {
    key: "appearance",
    label: "Appearance",
    iconKey: "Palette",
    href: "/settings/appearance",
    consolidated: true,
    items: [
        { href: "/settings/appearance#theme", label: "Theme", anchor: "theme" },
        { href: "/settings/appearance#density", label: "Density", anchor: "density" },
        { href: "/settings/appearance#accent", label: "Accent", anchor: "accent" },
    ],
};
function railGroupToNav(key, iconKey, group) {
    return {
        key,
        label: group.label,
        iconKey,
        href: group.pages[0]?.href ?? "/settings",
        consolidated: false,
        items: group.pages.map((p) => ({
            href: p.href,
            label: p.label,
            activeHref: p.activeHref,
        })),
    };
}
/**
 * Build the full settings nav model. Order is significant (rail order):
 * User · Workspace · Appearance · Multiplayer · Integrations · Agents ·
 * Apps · Advanced. Customers and Personal stay exported as rail-group
 * constants (routes and extract path #45996 live) but are not in the
 * buyer-facing rail — Phase 2 hide, not a teardown.
 */
export function buildSettingsNav() {
    const out = [
        USER_NAV_GROUP,
        WORKSPACE_NAV_GROUP,
        APPEARANCE_NAV_GROUP,
    ];
    // Multiplayer — the multi-device / team sub-app (own dashboard + sub-pages).
    out.push(railGroupToNav("team", "Users", MULTIPLAYER_SETTINGS_GROUP));
    out.push(railGroupToNav("integrations", "Plug", INTEGRATIONS_SETTINGS_GROUP));
    out.push(railGroupToNav("agents", "Bot", AGENTS_SETTINGS_GROUP));
    // Apps — the local-project launcher/runner config (docs/apps/PRD.md §7).
    out.push(railGroupToNav("apps", "AppWindow", APPS_SETTINGS_GROUP));
    out.push(railGroupToNav("advanced", "Wrench", ADVANCED_SETTINGS_GROUP));
    return out;
}
